import { useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { PageHeader } from '../../../components/ui/PageHeader'
import { Spinner } from '../../../components/ui/Spinner'
import { Modal } from '../../../components/ui/Modal'
import { Pagination } from '../../../components/ui/Pagination'
import { Alert } from '../../../components/ui/Alert'
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog'
import { FormField, inputClass, selectClass } from '../../../components/ui/FormField'
import {
  useModalidades, useCreateModalidade, useUpdateModalidade, useDeleteModalidade,
  useRegrasDesempate, useAplicarPadrao, useDeleteRegraDesempate, useCreateRegraDesempate,
} from '../hooks/useModalidades'
import { useCampeonatos } from '../../campeonatos/hooks/useCampeonatos'
import { modalidadeFormSchema, type ModalidadeFormData } from '../schemas'
import type { Modalidade, RegraDesempate } from '../types'

export function ModalidadesPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const preloadCampeonatoId = searchParams.get('idcampeonato') ? parseInt(searchParams.get('idcampeonato')!, 10) : undefined

  const [page, setPage] = useState(1)
  const [filterCampeonato, setFilterCampeonato] = useState(preloadCampeonatoId)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Modalidade | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Modalidade | null>(null)
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [regrasModal, setRegrasModal] = useState<number | null>(null)

  const { data, isLoading } = useModalidades({ idcampeonato: filterCampeonato, page, limit: 20 })
  const { data: campeonatosData } = useCampeonatos({ limit: 200 })
  const createModalidade = useCreateModalidade()
  const updateModalidade = useUpdateModalidade()
  const deleteModalidade = useDeleteModalidade()

  const form = useForm<ModalidadeFormData>({
    resolver: zodResolver(modalidadeFormSchema),
    defaultValues: {
      idcampeonato: preloadCampeonatoId || 0, grupo: 'IPSC', nome: '', divisao: '',
      numero_alvo_serie: null, qtd_municao: null, permite_reinscricao: false, max_reinscricao: 0, calcula_ranking: true, valor_x: 0,
      power_factor: 'minor',
    },
  })

  const grupoSelecionado = form.watch('grupo')

  function openCreate() {
    setEditing(null)
    form.reset({
      idcampeonato: filterCampeonato || 0, grupo: 'IPSC', nome: '', divisao: '',
      numero_alvo_serie: null, qtd_municao: null, permite_reinscricao: false, max_reinscricao: 0, calcula_ranking: true, valor_x: 0,
      power_factor: 'minor',
    })
    setModalOpen(true)
  }

  function openEdit(m: Modalidade) {
    setEditing(m)
    form.reset({
      idcampeonato: Number(m.idcampeonato), grupo: m.grupo, nome: m.nome,
      divisao: m.divisao ?? '', numero_alvo_serie: m.numero_alvo_serie, qtd_municao: m.qtd_municao,
      permite_reinscricao: m.permite_reinscricao, max_reinscricao: Number(m.max_reinscricao),
      calcula_ranking: m.calcula_ranking, valor_x: m.valor_x != null ? Number(m.valor_x) : 0,
      power_factor: m.power_factor ?? 'minor',
    })
    setModalOpen(true)
  }

  async function handleSubmit(formData: ModalidadeFormData) {
    try {
      if (editing) {
        await updateModalidade.mutateAsync({ id: editing.idmodalidade, data: formData })
        setAlert({ type: 'success', message: 'Modalidade atualizada com sucesso' })
      } else {
        await createModalidade.mutateAsync(formData as any)
        setAlert({ type: 'success', message: 'Modalidade cadastrada com sucesso' })
      }
      setModalOpen(false)
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao salvar modalidade' })
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    try {
      await deleteModalidade.mutateAsync(deleteTarget.idmodalidade)
      setAlert({ type: 'success', message: 'Modalidade excluida com sucesso' })
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao excluir modalidade' })
    } finally {
      setDeleteTarget(null)
    }
  }

  const meta = data?.meta
  const isSaving = createModalidade.isPending || updateModalidade.isPending

  const grupoColors: Record<string, string> = {
    IPSC: 'bg-orange-100 text-orange-800',
    Silhueta: 'bg-green-100 text-green-800',
    Precisao: 'bg-compumaq-dourado/15 text-compumaq-doradoClaro',
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Modalidades"
        description="Gerenciar modalidades dos campeonatos"
        action={
          <button onClick={openCreate} className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro">
            + Nova Modalidade
          </button>
        }
      />

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Campeonato</label>
            <select className={selectClass} value={filterCampeonato || ''}
              onChange={e => { setFilterCampeonato(parseInt(e.target.value, 10) || undefined); setPage(1) }}>
              <option value="">Todos os campeonatos</option>
              {campeonatosData?.data?.map(c => (
                <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao} ({c.temporada})</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : !data?.data?.length ? (
          <div className="text-center py-16 text-compumaq-textoSuave">
            <p className="text-4xl mb-3">🎯</p>
            <p className="font-medium">Nenhuma modalidade encontrada</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Nome</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Grupo</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Divisao</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Municao/prova</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Campeonato</th>
                  <th className="text-right px-4 py-3 font-medium text-compumaq-textoSuave">Acoes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-compumaq-dourado/25">
                {data.data.map((m) => (
                  <tr key={m.idmodalidade} className="hover:bg-compumaq-preto">
                    <td className="px-4 py-3 font-medium text-compumaq-texto">{m.nome}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${grupoColors[m.grupo] ?? 'bg-compumaq-pretoSuave text-compumaq-texto'}`}>
                        {m.grupo}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-compumaq-textoSuave">{m.divisao ?? '—'}</td>
                    <td className="px-4 py-3 text-compumaq-textoSuave">{m.qtd_municao ?? '—'}</td>
                    <td className="px-4 py-3 text-compumaq-textoSuave text-xs">{m.campeonato_descricao}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setRegrasModal(m.idmodalidade)}
                          className="text-xs text-purple-600 hover:text-purple-800 font-medium">Desempate</button>
                        <button onClick={() => navigate(`/etapas?idmodalidade=${m.idmodalidade}`)}
                          className="text-xs text-green-600 hover:text-green-800 font-medium">Etapas</button>
                        <button onClick={() => openEdit(m)}
                          className="text-xs text-compumaq-doradoClaro hover:text-compumaq-doradoClaro font-medium">Editar</button>
                        <button onClick={() => setDeleteTarget(m)}
                          className="text-xs text-red-600 hover:text-red-800 font-medium">Excluir</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {meta && meta.totalPages > 1 && (
              <Pagination page={page} totalPages={meta.totalPages} total={meta.total} limit={20} onPageChange={setPage} />
            )}
          </>
        )}
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Editar Modalidade' : 'Nova Modalidade'} size="lg">
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <FormField label="Campeonato" required error={form.formState.errors.idcampeonato?.message}>
            <Controller control={form.control} name="idcampeonato" render={({ field }) => (
              <select className={selectClass} value={field.value || ''} onChange={e => field.onChange(parseInt(e.target.value, 10) || 0)}>
                <option value="">Selecione...</option>
                {campeonatosData?.data?.map(c => (
                  <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao} ({c.temporada})</option>
                ))}
              </select>
            )} />
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Grupo" required error={form.formState.errors.grupo?.message}>
              <Controller control={form.control} name="grupo" render={({ field }) => (
                <select className={selectClass} value={field.value} onChange={field.onChange}>
                  {['IPSC', 'Silhueta', 'Precisao'].map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              )} />
            </FormField>
            <FormField label="Nome" required error={form.formState.errors.nome?.message}>
              <input className={inputClass} {...form.register('nome')} placeholder="Nome da modalidade" />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Divisao" error={form.formState.errors.divisao?.message}
              hint={grupoSelecionado === 'IPSC' ? 'No IPSC o atirador escolhe a divisao na inscricao (lista em IPSC > Divisoes) - deixe vazio' : undefined}>
              <input className={inputClass} {...form.register('divisao')} placeholder={grupoSelecionado === 'IPSC' ? 'Vazio (escolhida na inscricao)' : 'Ex: Standard'} />
            </FormField>
            <FormField label="N. alvos por serie" error={form.formState.errors.numero_alvo_serie?.message}>
              <input className={inputClass} type="number" min={1} {...form.register('numero_alvo_serie', { setValueAs: v => v === '' ? null : parseInt(v, 10) })} />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Quantidade de municao por prova" error={form.formState.errors.qtd_municao?.message}>
              <input className={inputClass} type="number" min={1} {...form.register('qtd_municao', { setValueAs: v => v === '' ? null : parseInt(v, 10) })}
                placeholder="Ex: 20" />
            </FormField>
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" {...form.register('permite_reinscricao')} className="rounded border-compumaq-dourado/25" />
              Permite reinscricao
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" {...form.register('calcula_ranking')} className="rounded border-compumaq-dourado/25" />
              Calcula ranking
            </label>
          </div>
          <FormField label="Valor do X (pontos, 0 = nao usa X)" error={form.formState.errors.valor_x?.message}>
            <input className={inputClass} type="number" min={0} max={99}
              {...form.register('valor_x', { setValueAs: v => v === '' || v == null ? 0 : parseInt(v, 10) })}
              placeholder="0 para Silhueta/sem X, 10 para Precisao" />
          </FormField>
          {grupoSelecionado === 'IPSC' && (
            <FormField label="Power Factor (define pontos por zona A/C/D)" error={form.formState.errors.power_factor?.message}
              hint="Na etapa com pistas vale o Power Factor que o atirador declarou na inscricao; este so vale no resultado unico (sem pistas)">
              <Controller control={form.control} name="power_factor" render={({ field }) => (
                <select className={selectClass} value={field.value ?? 'minor'} onChange={field.onChange}>
                  <option value="minor">Minor (A=5, C=3, D=1)</option>
                  <option value="major">Major (A=5, C=4, D=2)</option>
                </select>
              )} />
            </FormField>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setModalOpen(false)}
              className="px-4 py-2 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto">Cancelar</button>
            <button type="submit" disabled={isSaving}
              className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro disabled:opacity-50">
              {isSaving ? 'Salvando...' : editing ? 'Atualizar' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </Modal>

      {regrasModal && <RegrasDesempateModal idmodalidade={regrasModal} onClose={() => setRegrasModal(null)} />}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Excluir Modalidade"
        message={`Excluir "${deleteTarget?.nome}"?`}
        loading={deleteModalidade.isPending}
      />
    </div>
  )
}

function RegrasDesempateModal({ idmodalidade, onClose }: { idmodalidade: number; onClose: () => void }) {
  const { data: regras, isLoading } = useRegrasDesempate(idmodalidade)
  const aplicarPadrao = useAplicarPadrao()
  const deleteRegra = useDeleteRegraDesempate()
  const createRegra = useCreateRegraDesempate()
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [novaRegra, setNovaRegra] = useState({ campo: '', direcao: 'desc', tipo_campo: 'inteiro', ordem: 1, ativo: true })

  async function handleAplicarPadrao() {
    try {
      await aplicarPadrao.mutateAsync(idmodalidade)
      setAlert({ type: 'success', message: 'Regras padrao aplicadas com sucesso' })
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro' })
    }
  }

  async function handleAddRegra(e: React.FormEvent) {
    e.preventDefault()
    if (!novaRegra.campo.trim()) { setAlert({ type: 'error', message: 'Campo obrigatorio' }); return }
    try {
      await createRegra.mutateAsync({ idmodalidade, data: { ...novaRegra, ordem: Number(novaRegra.ordem) } })
      setNovaRegra({ campo: '', direcao: 'desc', tipo_campo: 'inteiro', ordem: (regras?.length ?? 0) + 2, ativo: true })
      setAlert({ type: 'success', message: 'Regra adicionada' })
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao adicionar regra' })
    }
  }

  const camposSugeridos = [
    'total_pontos', 'qtd_x', 'qtd_10', 'qtd_9', 'qtd_8', 'qtd_7', 'qtd_6',
    'qtd_5', 'qtd_4', 'qtd_3', 'qtd_2', 'qtd_1',
    'total', 'carneiro', 'peru', 'porco', 'galinha',
    'hit_factor', 'qtd_a', 'qtd_c', 'qtd_d', 'idade',
  ]

  return (
    <Modal open title="Regras de Desempate" onClose={onClose} size="lg">
      {alert && <div className="mb-4"><Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} /></div>}

      {/* Formulario para nova regra */}
      <form onSubmit={handleAddRegra} className="bg-compumaq-preto rounded-lg p-3 mb-4 border border-compumaq-dourado/25">
        <p className="text-xs font-semibold text-compumaq-textoSuave mb-2">Adicionar Regra</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
          <div>
            <label className="block text-xs text-compumaq-textoSuave mb-1">Campo</label>
            <select className="w-full px-2 py-1.5 border border-compumaq-dourado/25 rounded text-xs"
              value={novaRegra.campo} onChange={e => setNovaRegra(r => ({ ...r, campo: e.target.value }))}>
              <option value="">Selecione...</option>
              {camposSugeridos.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-compumaq-textoSuave mb-1">Direcao</label>
            <select className="w-full px-2 py-1.5 border border-compumaq-dourado/25 rounded text-xs"
              value={novaRegra.direcao} onChange={e => setNovaRegra(r => ({ ...r, direcao: e.target.value }))}>
              <option value="desc">desc (maior primeiro)</option>
              <option value="asc">asc (menor primeiro)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-compumaq-textoSuave mb-1">Tipo</label>
            <select className="w-full px-2 py-1.5 border border-compumaq-dourado/25 rounded text-xs"
              value={novaRegra.tipo_campo} onChange={e => setNovaRegra(r => ({ ...r, tipo_campo: e.target.value }))}>
              <option value="inteiro">inteiro</option>
              <option value="decimal">decimal</option>
              <option value="idade">idade</option>
              <option value="texto">texto</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-compumaq-textoSuave mb-1">Ordem</label>
            <input type="number" min={1} className="w-full px-2 py-1.5 border border-compumaq-dourado/25 rounded text-xs"
              value={novaRegra.ordem} onChange={e => setNovaRegra(r => ({ ...r, ordem: parseInt(e.target.value) || 1 }))} />
          </div>
        </div>
        <div className="flex justify-between items-center">
          <button type="button" onClick={handleAplicarPadrao} disabled={aplicarPadrao.isPending}
            className="px-3 py-1.5 border border-purple-300 text-purple-700 text-xs rounded hover:bg-purple-50 disabled:opacity-50">
            {aplicarPadrao.isPending ? 'Aplicando...' : 'Aplicar Regras Padrao'}
          </button>
          <button type="submit" disabled={createRegra.isPending}
            className="px-3 py-1.5 bg-compumaq-dourado text-compumaq-texto text-xs rounded hover:bg-compumaq-doradoClaro disabled:opacity-50">
            {createRegra.isPending ? 'Adicionando...' : '+ Adicionar Regra'}
          </button>
        </div>
      </form>

      {/* Tabela de regras existentes */}
      {isLoading ? <Spinner /> : !regras?.length ? (
        <p className="text-center text-compumaq-textoSuave py-4 text-sm">Nenhuma regra configurada. Use "Aplicar Regras Padrao" para comecar.</p>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full text-sm border border-compumaq-dourado/25 rounded-lg overflow-hidden">
          <thead className="bg-compumaq-preto">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-compumaq-textoSuave">Ordem</th>
              <th className="text-left px-3 py-2 font-medium text-compumaq-textoSuave">Campo</th>
              <th className="text-left px-3 py-2 font-medium text-compumaq-textoSuave">Direcao</th>
              <th className="text-left px-3 py-2 font-medium text-compumaq-textoSuave">Tipo</th>
              <th className="text-left px-3 py-2 font-medium text-compumaq-textoSuave">Ativo</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-compumaq-dourado/25">
            {regras.map((r: RegraDesempate) => (
              <tr key={r.idregra_desempate}>
                <td className="px-3 py-2">{r.ordem}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.campo}</td>
                <td className="px-3 py-2">
                  <span className={`text-xs px-1.5 py-0.5 rounded ${r.direcao === 'desc' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {r.direcao}
                  </span>
                </td>
                <td className="px-3 py-2 text-compumaq-textoSuave">{r.tipo_campo}</td>
                <td className="px-3 py-2">{r.ativo ? '✓' : '✗'}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => deleteRegra.mutate({ id: r.idregra_desempate, idmodalidade })}
                    className="text-xs text-red-600 hover:text-red-800">Remover</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </Modal>
  )
}
